import type { Extension } from '@codemirror/state';
import type { JSONSchema7 } from 'json-schema';

import type { EditorZodSchema } from '../../../CodeEditorAdvanced/modes/json/types';

export interface ModeConfig {
  mode: () => Extension;
  linter: (schema?: EditorZodSchema, config?: Record<string, unknown>) => Extension[];
  extensions: (schema?: JSONSchema7, config?: Record<string, unknown>) => Extension[];
}

export interface ModeConfigs {
  json: ModeConfig;
  markdown: ModeConfig;
  sql: ModeConfig;
}

// State for tracking mode loading
export interface ModeLoadingState {
  isLoading: boolean;
  error?: Error;
  mode?: ModeConfig;
}

// Lazy loaders for each mode
const lazyModeLoaders = {
  json: () => import(/* webpackChunkName: "CodeEditorJsonMode" */ './json/jsonMode').then((module) => module.jsonMode),
  markdown: () =>
    import(/* webpackChunkName: "CodeEditorMarkdownMode" */ '../../../CodeEditorAdvanced/modes/markdownMode').then(
      (module) => module.markdownMode,
    ),
  sql: () =>
    import(/* webpackChunkName: "CodeEditorSqlMode" */ '../../../CodeEditorAdvanced/modes/sqlMode').then(
      (module) => module.sqlMode,
    ),
} as const;

// Cache for loaded modes to avoid re-importing
const modeCache = new Map<keyof typeof lazyModeLoaders, ModeConfig>();

export type SupportedMode = keyof typeof lazyModeLoaders;

/**
 * Lazily loads a mode configuration based on the mode type
 * @param mode The mode to load ('json', 'markdown', or 'sql')
 * @returns Promise that resolves to the ModeConfig
 */
export const loadMode = async (mode: SupportedMode): Promise<ModeConfig> => {
  // Check cache first
  const cachedMode = modeCache.get(mode);
  if (cachedMode) {
    return cachedMode;
  }

  // Load the mode dynamically
  const loader = lazyModeLoaders[mode];
  const modeConfig = await loader();

  // Cache the loaded mode
  modeCache.set(mode, modeConfig);

  return modeConfig;
};
